import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { EntityService } from '../../../core/entity/entity.service';
import {
  ENTITY_TYPE_LABELS,
  ENTITY_TYPE_OPTIONS,
  EntityType,
} from '../../../core/entity/entity.types';
import { suggestTypeFromRegNo } from '../../../core/entity/entity-type-hint';
import { CompanyNameCheckComponent } from '../../../shared/company-name-check/company-name-check';
import { IconComponent } from '../../../shared/icon/icon';

type RegNoStatus = 'unchecked' | 'checking' | 'available' | 'taken';

/** Keep only digits and forward slashes; mirrors the backend normalize_reg_no. */
function normalizeRegNo(value: string | null | undefined): string {
  return (value ?? '').replace(/[^0-9/]/g, '');
}

function regNoFormat(control: AbstractControl): ValidationErrors | null {
  const raw = (control.value as string | null) ?? '';
  if (!raw) return null;
  const withoutWhitespace = raw.replace(/\s/g, '');
  if (withoutWhitespace === '') return { regNoFormat: true };
  return /^[0-9/]+$/.test(withoutWhitespace) ? null : { regNoFormat: true };
}

function tradingStartNotInFuture(group: AbstractControl): ValidationErrors | null {
  const yearCtrl = group.get('tradingStartYear');
  const monthCtrl = group.get('tradingStartMonth');
  const year = yearCtrl?.value as number | null;
  const month = monthCtrl?.value as number | null;
  if (year == null || month == null) return null;

  const now = new Date();
  const inFuture =
    year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);

  if (inFuture) {
    monthCtrl?.setErrors(
      { ...(monthCtrl.errors ?? {}), tradingStartInFuture: true },
      { emitEvent: false },
    );
  } else if (monthCtrl?.hasError('tradingStartInFuture')) {
    const { tradingStartInFuture: _drop, ...rest } = monthCtrl.errors!;
    monthCtrl.setErrors(Object.keys(rest).length ? rest : null, { emitEvent: false });
  }

  return inFuture ? { tradingStartInFuture: true } : null;
}

const CONSENT_TEXT_VERSION = '2026-11-01';

@Component({
  selector: 'app-agent-onboard-client',
  imports: [
    RouterLink,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    CompanyNameCheckComponent,
    IconComponent,
  ],
  templateUrl: './onboard-client.html',
  styleUrl: './onboard-client.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentOnboardClientComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly regNoStatus = signal<RegNoStatus>('unchecked');
  /** The name CIPC holds, while its question is unanswered (DOC-93 §1). */
  protected readonly nameToConfirm = signal<string | null>(null);
  private nameConfirmed = false;
  private askedFor = '';
  protected readonly isLoading = signal(false);
  protected readonly serverError = signal<string | null>(null);

  /** Template-facing alias for the shared vocabulary (see entity.types.ts). */
  protected readonly ENTITY_TYPES = ENTITY_TYPE_OPTIONS;
  protected readonly TYPE_LABELS = ENTITY_TYPE_LABELS;

  /**
   * The type the registration number's suffix suggests, where it differs from
   * the selection (FLOW-342). A hint only — never selected on the agent's
   * behalf; the CIPC check at Ops is where the form is confirmed.
   */
  protected typeHint(): EntityType | null {
    const suggested = suggestTypeFromRegNo(this.form.controls.regNo.value);
    return suggested !== null && suggested !== this.form.controls.type.value ? suggested : null;
  }

  protected readonly MONTHS: { value: number; label: string }[] = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  protected readonly form = this.fb.group(
    {
      name: ['', [Validators.required, Validators.maxLength(255)]],
      tradingName: ['', Validators.maxLength(255)],
      type: ['PTY' as EntityType, Validators.required],
      regNo: ['', [Validators.required, Validators.maxLength(50), regNoFormat]],
      industry: ['', [Validators.required, Validators.maxLength(255)]],
      tradingStartMonth: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1),
        Validators.max(12),
      ]),
      tradingStartYear: this.fb.control<number | null>(null, [
        Validators.required,
        Validators.min(1900),
        Validators.max(new Date().getFullYear()),
      ]),
      clientFirstName: ['', [Validators.required, Validators.maxLength(150)]],
      clientLastName: ['', [Validators.required, Validators.maxLength(150)]],
      clientEmail: ['', [Validators.required, Validators.email]],
      consent: [false, Validators.requiredTrue],
    },
    { validators: tradingStartNotInFuture },
  );

  private attestedAt: string | null = null;

  ngOnInit(): void {
    this.form.controls.regNo.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.nameToConfirm.set(null);
        this.nameConfirmed = false;
        this.askedFor = '';
        if (this.regNoStatus() !== 'unchecked') {
          this.regNoStatus.set('unchecked');
          this.form.controls.regNo.setErrors(null);
        }
      });

    this.form.controls.name.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((val) => {
        if (this.form.controls.tradingName.pristine) {
          this.form.controls.tradingName.setValue(val ?? '', { emitEvent: false });
        }
      });
  }

  /**
   * The name CIPC holds, once the number is known to be free. No answer changes
   * nothing and says nothing; what the person typed is never overwritten.
   */
  private lookUpCompany(regNo: string): void {
    if (this.askedFor === regNo) return;
    this.askedFor = regNo;
    this.entityService
      .companyLookup(regNo)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lookup) => {
          if (normalizeRegNo(this.form.controls.regNo.value) !== lookup.reg_no) return;
          this.nameToConfirm.set(lookup.registered_name ?? null);
          this.cdr.markForCheck();
        },
        error: () => undefined,
      });
  }

  /** On *No* nothing is kept and the person corrects the number. */
  protected answerNameCheck(answer: 'YES' | 'NO'): void {
    this.nameToConfirm.set(null);
    this.nameConfirmed = answer === 'YES';
    if (answer === 'NO') document.getElementById('entityRegNo')?.focus();
  }

  protected onRegNoBlur(): void {
    if (this.form.controls.regNo.hasError('regNoFormat')) return;

    const val = normalizeRegNo(this.form.controls.regNo.value);
    if (!val) return;

    const yearMatch = val.match(/^(\d{4})\//);
    if (yearMatch) {
      const regYear = parseInt(yearMatch[1], 10);
      if (
        regYear >= 1900 &&
        regYear <= new Date().getFullYear() &&
        this.form.controls.tradingStartYear.value === null
      ) {
        this.form.controls.tradingStartYear.setValue(regYear);
      }
    }

    this.regNoStatus.set('checking');
    this.cdr.markForCheck();

    this.entityService
      .checkRegNo(val)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.available) {
            this.regNoStatus.set('available');
            this.form.controls.regNo.setErrors(null);
            this.lookUpCompany(val);
          } else {
            this.regNoStatus.set('taken');
            this.form.controls.regNo.setErrors({ taken: true });
          }
          this.cdr.markForCheck();
        },
        error: () => {
          this.regNoStatus.set('unchecked');
          this.cdr.markForCheck();
        },
      });
  }

  protected onConsentChange(checked: boolean): void {
    this.attestedAt = checked ? new Date().toISOString() : null;
  }

  /**
   * Disabled only while something is in flight. An incomplete form stays clickable so
   * pressing submit outlines every missing field, rather than greying out with no reason.
   */
  protected get isSubmitDisabled(): boolean {
    return this.isLoading() || this.regNoStatus() === 'checking';
  }

  protected onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.isSubmitDisabled || this.form.invalid || this.regNoStatus() === 'taken') return;

    this.serverError.set(null);
    this.isLoading.set(true);

    const v = this.form.getRawValue();

    const payload = {
      name: v.name!,
      reg_no: normalizeRegNo(v.regNo),
      type: v.type! as EntityType,
      industry: v.industry!,
      trading_start_month: v.tradingStartMonth!,
      trading_start_year: v.tradingStartYear!,
      client_first_name: v.clientFirstName!,
      client_last_name: v.clientLastName!,
      client_email: v.clientEmail!,
      preparer_attestation: {
        consent_text_version: CONSENT_TEXT_VERSION,
        attested_at: this.attestedAt ?? new Date().toISOString(),
      },
      ...(v.tradingName ? { trading_name: v.tradingName } : {}),
      ...(this.nameConfirmed ? { company_name_confirmed: true } : {}),
    };

    this.entityService
      .onboardClient(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entity) => {
          void this.router.navigate(['/entity', entity.short_id, 'dashboard'], {
            queryParams: { onboarded: 1 },
          });
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading.set(false);
          if (err.status === 409) {
            this.regNoStatus.set('taken');
            this.form.controls.regNo.setErrors({ taken: true });
          } else if (err.status === 400 && err.error?.detail) {
            this.applyServerErrors(err.error.detail as Record<string, string | string[]>);
          } else {
            this.serverError.set(
              typeof err.error?.detail === 'string'
                ? err.error.detail
                : 'Could not add this client. Please try again.',
            );
          }
          this.cdr.markForCheck();
        },
      });
  }

  private applyServerErrors(errors: Record<string, string | string[]>): void {
    const fieldMap: Record<string, string> = {
      name: 'name',
      trading_name: 'tradingName',
      reg_no: 'regNo',
      type: 'type',
      industry: 'industry',
      trading_start_month: 'tradingStartMonth',
      trading_start_year: 'tradingStartYear',
      client_first_name: 'clientFirstName',
      client_last_name: 'clientLastName',
      client_email: 'clientEmail',
    };
    const controlNames = Object.entries(errors).map(([backendField, message]) => {
      const controlName = fieldMap[backendField] ?? backendField;
      const errorText = Array.isArray(message) ? message[0] : message;
      this.form.get(controlName)?.setErrors({ serverError: errorText });
      return controlName;
    });
    this.cdr.markForCheck();
    // On a short laptop screen the submit button sits well below the field the
    // server refused, so bring that field and its message into view.
    const first = document.getElementById(controlNames[0] ?? '');
    first?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    first?.focus({ preventScroll: true });
  }
}
